import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { Form, FormGroup, Label, Input, Table, InputGroup, InputGroupText, Alert } from 'reactstrap';
import { useFieldArray, useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { InvoicePaymentsDto, InvoicingDetailDto } from '../../../types';
import { toast } from 'react-toastify';
import { invoiceService } from '../../../services/InvoiceService';
import * as yup from 'yup';
import moment from 'moment';
import { Button } from '../../../components';
import { PaymentTypes, PaymentType } from '../../../common/Data';

const currencyFormatter = new Intl.NumberFormat(`en-US`, {
    style: `currency`,
    currency: `USD`,
});

interface InvoicePaymentFormProps {
    invoice: InvoicingDetailDto;
    onSave: (payment: InvoicePaymentsDto) => void;
    onCancel: () => void;
    isRefund: boolean;
}

// Form data interface with proper typing
interface InvoicePaymentFormData {
    id?: number;
    invoiceId: number;
    transactionId?: string;
    paymentType: number;
    paymentAmount: number;
    convenienceFee?: number;
    paidBy?: string;
    referenceNumber: string;
    checkInformation?: string;
    rowVersion?: string;
    firstName?: string;
    lastName?: string;
    address?: string;
    city?: string;
    state?: string;
    zipCode?: string;
    email?: string;
    createDate?: string;
    paymentDate: string;
    country?: string;
    isConfirmed?: boolean;
    isDeleted?: boolean;
    isRefund?: boolean;
    professionIds?: number[];
    receiptUrl?: string;
    professionAmounts: { id: number; amountPaid?: number }[];
}

const InvoicePaymentForm = (props: InvoicePaymentFormProps) => {
    const [amountError, setAmountError] = useState<string>(``);
    const [isUpdatingFromIndividual, setIsUpdatingFromIndividual] = useState(false);
    const [isUpdatingFromTotal, setIsUpdatingFromTotal] = useState(false);

    const formatMoneyOrZero = (amount: number | undefined | null): string => {
        return currencyFormatter.format(amount || 0);
    };

    const amountPaid = (inv: InvoicingDetailDto): number => {
        let total = 0;

        if (!!inv.invoicePayments) {
            inv.invoicePayments
                .filter((ip) => !ip.isDeleted)
                .forEach((ip) => {
                    total += +ip.paymentAmount;
                });
        }

        return total;
    };

    const calculateLateFeePaid = () => {
        let profAmts = 0;

        props.invoice.professionListDeserialized?.forEach((pl) => {
            profAmts += pl.amountPaid || 0;
        });
        return !!profAmts ? amountPaid(props.invoice) - profAmts : 0;
    };

    const [lateFeePaid, setLateFeePaid] = useState<number>(0);

    const schema = yup
        .object({
            id: yup.number().optional().nullable(),
            invoiceId: yup.number().required(),
            transactionId: yup.string().optional().nullable(),
            paymentType: yup.number().required(),
            paymentAmount: yup.number().required(),
            convenienceFee: yup.number().optional().nullable(),
            paidBy: yup.string().optional().nullable(),
            referenceNumber: yup.string(),
            checkInformation: yup.string().optional().nullable(),
            rowVersion: yup.string().optional().nullable(),
            firstName: yup.string().optional().nullable(),
            lastName: yup.string().optional().nullable(),
            address: yup.string().optional().nullable(),
            city: yup.string().optional().nullable(),
            state: yup.string().optional().nullable(),
            zipCode: yup.string().optional().nullable(),
            email: yup.string().optional().nullable(),
            createDate: yup.string().optional().nullable(),
            paymentDate: yup.string().required(),
            country: yup.string().optional().nullable(),
            isConfirmed: yup.boolean().optional().nullable(),
            isDeleted: yup.boolean().optional().nullable(),
            isRefund: yup.boolean().optional().nullable(),
            professionIds: yup.array().of(yup.number().required()).optional(),
            receiptUrl: yup.string().optional().nullable(),
            professionAmounts: yup
                .array()
                .of(
                    yup
                        .object<{ id: number; amountPaid?: number }>({
                            id: yup.number().required(),
                            amountPaid: yup.number().optional(),
                        })
                        .defined(),
                )
                .notRequired(),
        })
        .defined();

    const {
        handleSubmit,
        register,
        formState: { isSubmitting, errors },
        control,
        getValues,
        setValue,
        watch,
        reset,
    } = useForm({
        resolver: yupResolver(schema as any),
        defaultValues: {
            paymentDate: moment().format('YYYY-MM-DD'),
            paymentAmount: 0,
            invoiceId: props.invoice.id,
            isRefund: props.isRefund,
            paymentType: 0,
            referenceNumber: '',
            professionAmounts:
                props.invoice?.professionListDeserialized?.map((pl) => {
                    return {
                        id: pl.id,
                        amountPaid: 0,
                    };
                }) || [],
        } as InvoicePaymentsDto,
    });

    const { fields } = useFieldArray({
        control,
        name: 'professionAmounts',
    });

    const watchPaymentAmount = watch('paymentAmount');
    const watchProfessionAmounts = watch('professionAmounts');

    const getProfessionAmountPaid = useMemo(() => (plId: number) => {
        const pl = props.invoice.professionListDeserialized?.find((pl) => pl.id === plId);
        if (!!pl) {
            return pl.amountPaid;
        }
        return null;
    }, [props.invoice]);

    const getProfessionAmountDue = useMemo(() => (plId: number) => {
        const pl = props.invoice.professionListDeserialized?.find((pl) => pl.id === plId);
        if (!!pl) {
            return pl?.amountPaid === 0
                ? props.invoice?.amount || 0
                : (props.invoice?.amount || 0) - (pl?.amountPaid || 0);
        }
        return null;
    }, [props.invoice]);

    // Reset form when component mounts or invoice changes
    useEffect(() => {
        reset({
            paymentDate: moment().format('YYYY-MM-DD'),
            paymentAmount: 0,
            invoiceId: props.invoice.id,
            isRefund: props.isRefund,
            paymentType: 0,
            referenceNumber: '',
            professionAmounts:
                props.invoice?.professionListDeserialized?.map((pl) => {
                    return {
                        id: pl.id,
                        amountPaid: 0,
                    };
                }) || [],
        });
        setLateFeePaid(0);
        setIsUpdatingFromIndividual(false);
        setIsUpdatingFromTotal(false);
    }, [props.invoice.id, props.isRefund]);

    // Handle payment amount changes - distribute to programs
    useEffect(() => {
        // Skip if we're updating from individual amounts or no fields
        if (isUpdatingFromIndividual || !fields || fields.length === 0) {
            setIsUpdatingFromIndividual(false);
            return;
        }

        setIsUpdatingFromTotal(true);

        let remainingPayment = watchPaymentAmount || 0;

        // Handle late fee first if applicable
        let lateFeePayment = 0;
        if (props.invoice.lateFee && props.invoice.lateFee > 0 && !props.isRefund) {
            const lateFeeRemaining = (props.invoice.lateFee || 0) - calculateLateFeePaid();
            if (lateFeeRemaining > 0 && remainingPayment > 0) {
                lateFeePayment = Math.min(remainingPayment, lateFeeRemaining);
                setLateFeePaid(lateFeePayment);
                remainingPayment -= lateFeePayment;
            }
        }

        // Get programs with their amounts due
        const programs = fields.map((field, index) => {
            const professionId = getValues(`professionAmounts.${index}.id`);
            const amountDue = getProfessionAmountDue(professionId ?? 0) || 0;
            return { index, professionId, amountDue };
        });

        // Sort by amount due (smallest first) to fill smaller amounts completely
        programs.sort((a, b) => a.amountDue - b.amountDue);

        // Track what we're applying to each program
        const applications = new Map<number, number>();

        // First pass: Apply to programs up to their due amounts
        for (const program of programs) {
            if (remainingPayment <= 0) {
                applications.set(program.index, 0);
                continue;
            }

            const toApply = Math.min(remainingPayment, program.amountDue);
            applications.set(program.index, toApply);
            remainingPayment -= toApply;
        }

        // If there's still money left, distribute evenly among programs that have amounts due
        if (remainingPayment > 0) {
            const programsWithDue = programs.filter(p => p.amountDue > 0);

            if (programsWithDue.length > 0) {
                const extraPerProgram = remainingPayment / programsWithDue.length;

                for (const program of programsWithDue) {
                    const currentAmount = applications.get(program.index) || 0;
                    applications.set(program.index, currentAmount + extraPerProgram);
                }
            } else {
                // If no programs have amounts due, split evenly among all
                const extraPerProgram = remainingPayment / programs.length;
                for (const program of programs) {
                    const currentAmount = applications.get(program.index) || 0;
                    applications.set(program.index, currentAmount + extraPerProgram);
                }
            }
        }

        // Apply the calculated amounts
        for (let i = 0; i < fields.length; i++) {
            const amount = applications.get(i) || 0;
            setValue(`professionAmounts.${i}.amountPaid`, Math.round(amount * 100) / 100);
        }

        setTimeout(() => setIsUpdatingFromTotal(false), 100);
    }, [watchPaymentAmount]);

    // Handle individual amount changes - update total
    const handleIndividualAmountChange = (index: number, value: string) => {
        if (isUpdatingFromTotal) {
            return;
        }

        setIsUpdatingFromIndividual(true);
        const numValue = parseFloat(value) || 0;
        setValue(`professionAmounts.${index}.amountPaid`, numValue);

        // Calculate new total after a short delay to let the value update
        setTimeout(() => {
            const amounts = getValues('professionAmounts') || [];
            const total = amounts.reduce((sum, item) => sum + (parseFloat(String(item.amountPaid || 0)) || 0), 0);
            const totalWithLateFee = total + (lateFeePaid || 0);
            setValue('paymentAmount', Math.round(totalWithLateFee * 100) / 100);
        }, 0);
    };

    // Handle late fee change
    const handleLateFeeChange = (value: string) => {
        if (isUpdatingFromTotal) {
            return;
        }

        setIsUpdatingFromIndividual(true);
        const numValue = parseFloat(value) || 0;
        setLateFeePaid(numValue);

        // Calculate new total
        setTimeout(() => {
            const amounts = getValues('professionAmounts') || [];
            const total = amounts.reduce((sum, item) => sum + (parseFloat(String(item.amountPaid || 0)) || 0), 0);
            const totalWithLateFee = total + numValue;
            setValue('paymentAmount', Math.round(totalWithLateFee * 100) / 100);
        }, 0);
    };

    // Cancel handler
    const handleCancel = () => {
        reset({
            paymentDate: moment().format('YYYY-MM-DD'),
            paymentAmount: 0,
            invoiceId: props.invoice.id,
            isRefund: props.isRefund,
            paymentType: 0,
            referenceNumber: '',
            professionAmounts:
                props.invoice?.professionListDeserialized?.map((pl) => {
                    return {
                        id: pl.id,
                        amountPaid: 0,
                    };
                }) || [],
        });
        setLateFeePaid(0);
        setIsUpdatingFromIndividual(false);
        setIsUpdatingFromTotal(false);
        props.onCancel();
    };

    const getProgramTitleDetail = (plId: number) => {
        const pl = props.invoice.professionListDeserialized?.find((pl) => pl.id === plId);

        if (!!pl) {
            return (
                <td>
                    <span>{pl.title}</span>
                </td>
            );
        }
    };

    const onSubmit = (values: any) => {
        setAmountError(``);
        let totalPaid = lateFeePaid;

        if (lateFeePaid > (props.invoice.lateFee || 0) && !props.isRefund) {
            setAmountError(`A late fee has been applied when there is no late fee due.`);
            return;
        }

        values.professionAmounts?.forEach((pl) => {
            totalPaid += +(pl.amountPaid || 0);
        });

        const roundedTotal = Math.round(totalPaid * 100) / 100;
        const roundedPayment = Math.round(values.paymentAmount * 100) / 100;

        if (roundedTotal > roundedPayment) {
            setAmountError(
                `More has been allocated (${formatMoneyOrZero(
                    totalPaid,
                )}) than the amount paid towards the invoice (${formatMoneyOrZero(values.paymentAmount)})`,
            );
            return;
        }

        if (roundedTotal < roundedPayment) {
            setAmountError(
                `Less has been allocated (${formatMoneyOrZero(
                    totalPaid,
                )}) than the amount paid towards the invoice (${formatMoneyOrZero(values.paymentAmount)})`,
            );
            return;
        }

        const payments = values.professionAmounts;
        if (payments) {
            payments.forEach((val, i) => {
                const pl = props.invoice.professionListDeserialized?.find((pl) => pl.id === val.id);
                if (Number(props.invoice.amount) - Number(val.amountPaid) + Number(pl?.amountPaid) < 0) {
                    setAmountError(
                        `More has been allocated (${formatMoneyOrZero(
                            val.amountPaid,
                        )}) than the amount due for the program (${pl?.title})`,
                    );
                    return;
                }
            });
        }

        const toastId = toast.info('Saving Payment...');

        invoiceService
            .saveInvoicePayment({
                ...values,
                isConfirmed: true,
                professionAmounts: [...(values.professionAmounts || [])],
            })
            .then((response) => {
                toast.update(toastId, {
                    type: 'success',
                    render: 'Payment saved successfuly',
                });
                props.onSave(response);
            })
            .catch(() => {
                toast.update(toastId, {
                    type: 'error',
                    render: 'Error saving Payment',
                });
            });
    };

    return (
        <Form noValidate onSubmit={handleSubmit(onSubmit)}>
            <FormGroup>
                <Label for="paymentDate">{props.isRefund ? 'Refund' : 'Payment'} Date</Label>
                <Controller
                    name="paymentDate"
                    control={control}
                    render={({ field }) => (
                        <Input
                            type="date"
                            name={field.name}
                            onBlur={field.onBlur}
                            ref={field.ref}
                            value={
                                field.value
                                    ? typeof field.value === 'string'
                                        ? field.value
                                        : field.value.toISOString().split('T')[0]
                                    : ''
                            }
                            onChange={(e) => field.onChange(e.target.value)}
                        />
                    )}
                />
                {errors?.paymentDate?.message && (
                    <span className={'text-danger'}>{errors.paymentDate.message}</span>
                )}
            </FormGroup>
            <FormGroup>
                <Label for="paymentType">{props.isRefund ? 'Refund' : 'Payment'} Type</Label>
                <Controller
                    name="paymentType"
                    control={control}
                    render={({ field }) => (
                        <Input
                            type="select"
                            {...field}
                            value={field.value || 0}
                            onChange={(e) => field.onChange(Number(e.target.value))}
                        >
                            <option value={0}>Select...</option>
                            {PaymentTypes.filter(props.isRefund ? (x) => x : (x) => x !== 'Credit Card').map((ct) => (
                                <option key={ct} value={PaymentType[ct]}>
                                    {ct}
                                </option>
                            ))}
                        </Input>
                    )}
                />
                {errors?.paymentType?.message && (
                    <span className={'text-danger'}>{errors.paymentType.message}</span>
                )}
            </FormGroup>
            <FormGroup>
                <Label for="referenceNumber">Reference #</Label>
                <Controller
                    name="referenceNumber"
                    control={control}
                    render={({ field }) => <Input {...field} value={field.value || ''} />}
                />
            </FormGroup>
            <FormGroup>
                <Label for="paymentAmount">Payment Amount</Label>
                <InputGroup>
                    <InputGroupText>$</InputGroupText>
                    <Controller
                        name="paymentAmount"
                        control={control}
                        render={({ field }) => (
                            <Input
                                type="number"
                                step="0.01"
                                {...field}
                                value={field.value || ''}
                                onChange={(e) => {
                                    const value = parseFloat(e.target.value) || 0;
                                    field.onChange(value);
                                }}
                            />
                        )}
                    />
                </InputGroup>
                {errors?.paymentAmount?.message && (
                    <span className={'text-danger'}>{errors.paymentAmount.message}</span>
                )}
            </FormGroup>
            <FormGroup>
                <Label for="alreadyPaid">Amount Already Paid</Label>
                <span className={`ms-3`}>{formatMoneyOrZero(amountPaid(props.invoice))}</span>
            </FormGroup>
            <FormGroup>
                <Label for="paymentAmount">{props.isRefund ? 'Refund' : 'Payment'} Distribution</Label>
                <Table className={`mb-3`}>
                    <thead>
                    <tr>
                        <th>Program</th>
                        <th>Amount Due</th>
                        <th>Amount Paid</th>
                    </tr>
                    </thead>
                    <tbody>
                    {fields.map((f, i) => {
                        const watchedProfessionAmounts = watch('professionAmounts');
                        const professionId = watchedProfessionAmounts?.[i]?.id || 0;
                        const amountDue = getProfessionAmountDue(+professionId);

                        return (
                            <tr key={f.id}>
                                {getProgramTitleDetail(+professionId)}
                                <td
                                    style={
                                        Number(getProfessionAmountPaid(+professionId)) !== 0
                                            ? { fontWeight: 'bold' }
                                            : { fontWeight: 'normal' }
                                    }
                                >
                                    {formatMoneyOrZero(amountDue)}
                                </td>
                                <td>
                                    <InputGroup>
                                        <InputGroupText>$</InputGroupText>
                                        <Controller
                                            name={`professionAmounts.${i}.amountPaid`}
                                            control={control}
                                            render={({ field }) => (
                                                <Input
                                                    type="number"
                                                    step="0.01"
                                                    readOnly={amountDue === 0 && !props.isRefund}
                                                    {...field}
                                                    value={field.value || ''}
                                                    onChange={(e) => {
                                                        field.onChange(e.target.value);
                                                        handleIndividualAmountChange(i, e.target.value);
                                                    }}
                                                />
                                            )}
                                        />
                                    </InputGroup>
                                    <Controller
                                        name={`professionAmounts.${i}.id`}
                                        control={control}
                                        render={({ field }) => (
                                            <input type={`hidden`} {...field} />
                                        )}
                                    />
                                </td>
                            </tr>
                        );
                    })}
                    {(Number(props.invoice.lateFee) > 0 || lateFeePaid !== 0 || props.isRefund) ? (
                        <tr>
                            <td>Administrative Fee</td>
                            <td>{formatMoneyOrZero((props?.invoice?.lateFee || 0) - calculateLateFeePaid())}</td>
                            <td>
                                <InputGroup>
                                    <InputGroupText>$</InputGroupText>
                                    <Input
                                        type="number"
                                        step="0.01"
                                        value={lateFeePaid || ''}
                                        onChange={(e) => handleLateFeeChange(e.target.value)}
                                    />
                                </InputGroup>
                            </td>
                        </tr>
                    ) : null}
                    </tbody>
                </Table>
                {amountError && (
                    <Alert className={`alert-danger`} transition={{ timeout: 0 }}>
                        {amountError}
                    </Alert>
                )}
            </FormGroup>
            <input type="hidden" {...register('invoiceId')} />
            <input type="hidden" {...register('isRefund')} />
            <div>
                <Button type="button" color={'link'} onClick={handleCancel}>
                    Cancel
                </Button>
                <Button disabled={isSubmitting} type={'submit'} color={'primary'}>{`Save`}</Button>
            </div>
        </Form>
    );
};

export default InvoicePaymentForm;